import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays } from "lucide-react";
import Seo from "@/components/Seo";
import ShareButtons from "@/components/ShareButtons";
import { api } from "@/lib/api";
import { metaDescription, seoDesc } from "@/lib/seo";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
  }, [slug]);

  const articleJsonLd = post
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: metaDescription(seoDesc(post, "body"), 200),
        ...(post.image && { image: [new URL(post.image, window.location.origin).href] }),
        datePublished: post.created_at,
        ...(post.updated_at && { dateModified: post.updated_at }),
        author: { "@type": "Organization", name: "AB Hunter Consulting USA", url: window.location.origin },
        publisher: { "@type": "Organization", name: "AB Hunter Consulting USA", url: window.location.origin },
      }
    : undefined;

  if (notFound) {
    return (
      <div data-testid="blog-post-not-found" className="flex min-h-screen flex-col items-center justify-center bg-ink px-6 text-center text-foreground">
        <h1 className="font-display text-3xl font-bold">Post not found</h1>
        <p className="mt-3 text-sm text-muted-foreground">This post may have been unpublished or moved.</p>
        <Link to="/blog" data-testid="blog-notfound-back" className="mt-6 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-ink hover:bg-gold-hover">
          Back to the blog
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-ink text-foreground">
      <Seo
        title={post ? `${post.title} — AB Hunter Consulting USA` : "Blog — AB Hunter Consulting USA"}
        siteName="AB Hunter Consulting USA"
        description={post ? seoDesc(post, "body") : "AB Hunter Consulting USA blog."}
        image={post ? post.image || "/assets/academy-1.png" : undefined}
        type="article"
        jsonLd={articleJsonLd}
      />
      <article className="relative mx-auto max-w-3xl px-5 pb-24 pt-24 sm:px-8 lg:pt-16">
        <Link to="/blog" data-testid="blog-post-back" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-gold">
          <ArrowLeft className="h-4 w-4" /> All posts
        </Link>

        {post && (
          <>
            <p className="mt-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              <CalendarDays className="h-3.5 w-3.5" /> {fmtDate(post.created_at)}
            </p>
            <h1 data-testid="blog-post-title" className="mt-3 font-display text-4xl font-bold leading-tight sm:text-5xl">{post.title}</h1>
            {post.image && (
              <div className="mt-8 overflow-hidden rounded-3xl border border-gold/15">
                <img src={post.image} alt={post.title} className="w-full object-cover" />
              </div>
            )}
            <div data-testid="blog-post-body" className="mt-8 space-y-5 text-base leading-relaxed text-foreground/85">
              {post.body.split(/\n{2,}/).filter(Boolean).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
            <div className="mt-10 rounded-2xl border border-gold/15 bg-ink-card/60 p-6" data-testid="blog-share-box">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Share this post</p>
              <div className="mt-4">
                <ShareButtons testId="blog-share" title={post.title} />
              </div>
            </div>
          </>
        )}
      </article>
    </div>
  );
}
